import React, { useState } from 'react';
import clsx from 'clsx';

export type QuantitySelectorVariant = 'default' | 'pill';

export type QuantitySelectorProps = {
  defaultQuantity: number;
  maxQuantity?: number;
  minQuantity?: number;
  disabled?: boolean;
  onQuantityChange: (quantity: number) => void;
  // Optional accessible names. Callers that render the stepper without a
  // visible label pass localized strings; existing callers keep the plain
  // "-"/"+" text as the accessible name.
  decreaseLabel?: string;
  increaseLabel?: string;
  valueLabel?: string;
  // `pill` is the fully rounded stepper of the classic product detail panel;
  // `default` keeps the boxed stepper of the cart and return flows.
  variant?: QuantitySelectorVariant;
  // Lets the visitor type a quantity instead of stepping to it. Off by
  // default: the cart and the return flow persist every change, and a typed
  // "12" would reach them as "1" first.
  isEditable?: boolean;
};

interface QuantitySelectorVariantClasses {
  border: string;
  start: string;
  end: string;
}

const VARIANT_CLASSES: Record<QuantitySelectorVariant, QuantitySelectorVariantClasses> = {
  default: { border: 'border-neutral-10', start: 'rounded-l-md', end: 'rounded-r-md' },
  pill: { border: 'border-neutral-55', start: 'rounded-l-full', end: 'rounded-r-full' },
};

export default function QuantitySelector({
  defaultQuantity = 0,
  maxQuantity,
  minQuantity,
  disabled,
  onQuantityChange,
  decreaseLabel,
  increaseLabel,
  valueLabel,
  variant = 'default',
  isEditable = false,
}: Readonly<QuantitySelectorProps>) {
  const [quantity, setQuantity] = useState(defaultQuantity);
  const [draft, setDraft] = useState(String(defaultQuantity));
  const variantClasses = VARIANT_CLASSES[variant];

  const handleQuantityChange = (newQuantity: number) => {
    const isBelowMin = minQuantity !== undefined && newQuantity < minQuantity;
    const isAboveMax = maxQuantity !== undefined && newQuantity > maxQuantity;

    // only update the quantity if it's within the min and max range
    if (!isBelowMin && !isAboveMax && !disabled) {
      setQuantity(newQuantity);
      // The stepper buttons own the value as well, so the field follows them.
      setDraft(String(newQuantity));
      onQuantityChange(newQuantity); // hook for parent components
    }
  };

  /**
   * Applies what the visitor typed. An out-of-range or unreadable entry snaps
   * to the nearest allowed quantity rather than being rejected silently.
   */
  const commitDraft = () => {
    const parsed = Number.parseInt(draft, 10);

    if (Number.isNaN(parsed)) {
      setDraft(String(quantity));

      return;
    }

    const withMin = minQuantity === undefined ? parsed : Math.max(parsed, minQuantity);
    const clamped = maxQuantity === undefined ? withMin : Math.min(withMin, maxQuantity);

    setDraft(String(clamped));
    handleQuantityChange(clamped);
  };

  const valueClasses = clsx(
    'inline-flex w-10 items-center justify-center border-b border-t text-center',
    variantClasses.border,
  );

  return (
    <div
      className={clsx('flex h-10 font-medium text-neutral-80', {
        'cursor-not-allowed': disabled,
      })}
    >
      <button
        type="button"
        aria-label={decreaseLabel}
        onClick={() => handleQuantityChange(quantity - 1)}
        className={clsx(
          'inline-flex w-10 items-center justify-center border text-xl hover:bg-neutral-10',
          variantClasses.border,
          variantClasses.start,
          {
            'cursor-not-allowed opacity-50': quantity === minQuantity || disabled,
          },
        )}
      >
        -
      </button>

      {isEditable ? (
        <input
          type="text"
          inputMode="numeric"
          aria-label={valueLabel}
          value={draft}
          disabled={disabled}
          // Digits only, so the field never holds something it cannot commit.
          onChange={(event) => setDraft(event.target.value.replaceAll(/\D/g, ''))}
          onBlur={commitDraft}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commitDraft();
            }
          }}
          className={clsx(valueClasses, 'bg-transparent outline-none', {
            'cursor-not-allowed': disabled,
          })}
        />
      ) : (
        <span aria-label={valueLabel} className={valueClasses}>
          {quantity}
        </span>
      )}

      <button
        type="button"
        aria-label={increaseLabel}
        onClick={() => handleQuantityChange(quantity + 1)}
        className={clsx(
          'inline-flex w-10 items-center justify-center border text-xl hover:bg-neutral-10',
          variantClasses.border,
          variantClasses.end,
          {
            'cursor-not-allowed opacity-50': quantity === maxQuantity || disabled,
          },
        )}
      >
        +
      </button>
    </div>
  );
}
